import { forwardRef, useMemo, useState } from 'react'
import {Target, Zap} from 'lucide-react'
import SolverCard from '@/components/SolverCard'
import CopyButton from '@/components/CopyButton'
import EmailSolutionButton from '@/components/EmailSolutionButton'

interface Props { highlighted: boolean }

type Mode = 1 | 10 | 100

const RoundingCalculator = forwardRef<HTMLElement, Props>(({ highlighted }, ref) => {
  const [input, setInput] = useState('47.6')
  const [mode, setMode] = useState<Mode>(1)
  const [snapped, setSnapped] = useState(false)
  const [snapKey, setSnapKey] = useState(0)

  const num = Number.parseFloat(input)
  const valid = !Number.isNaN(num)

  const rounded = useMemo(() => {
    if (!valid) return 0
    return Math.round(num / mode) * mode
  }, [num, mode, valid])

  const trigger = () => {
    if (!valid) return
    setSnapped(false)
    setSnapKey(k => k + 1)
    requestAnimationFrame(() => setSnapped(true))
  }

  const md = useMemo(() => `# Rounding Numbers\n\n- Input: ${valid ? num : '—'}\n- Mode: Nearest ${mode}s\n- Half-up rule applied\n- Result: **${valid ? rounded : '—'}**`, [num, mode, rounded, valid])

  // Number line geometry
  const vw = 520, vh = 200
  const padX = 30
  const baseY = vh - 50

  // Build anchors around current value
  const anchorCount = 9
  const anchorStep = mode
  const center = valid ? Math.round(num / mode) * mode : 0
  const start = center - Math.floor(anchorCount / 2) * anchorStep
  const anchors = Array.from({ length: anchorCount }, (_, i) => start + i * anchorStep)

  const range = anchors[anchors.length - 1] - anchors[0] || 1
  const xFor = (v: number) => padX + ((v - anchors[0]) / range) * (vw - padX * 2)

  const ballStartX = valid ? xFor(num) : vw / 2
  const ballEndX = valid ? xFor(rounded) : vw / 2

  return (
    <SolverCard
      ref={ref}
      id="rounding"
      eyebrow="Arithmetic · 18,100/mo"
      title="Rounding Numbers Calculator | Snapping Number Line"
      volume="Vol 18.1K"
      highlighted={highlighted}
    >
      <p className="text-slate-400 text-sm md:text-[15px] leading-relaxed mb-6">
        Rounding uses the <strong className="text-slate-200">half-up rule</strong>: if the digit to the right of your target place is{' '}
        <span className="font-mono-math text-emerald-300">≥ 5</span>, round up; otherwise round down. Pick a place value, type any decimal, hit{' '}
        <span className="text-emerald-300">Trigger Snapping</span> — gravity pulls the ball down the slope onto the nearest anchor integer.
      </p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1.5">Source Number</label>
            <input
              type="number"
              value={input}
              onChange={e => setInput(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-[#0a0e1a] border border-[var(--line)] text-white font-mono-math text-2xl focus:outline-none focus:border-emerald-400/60"
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[1, 10, 100].map(m => (
              <button
                key={m}
                onClick={() => setMode(m as Mode)}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition ${
                  mode === m
                    ? 'border-emerald-400/60 bg-emerald-400/10 text-emerald-200'
                    : 'border-[var(--line)] bg-white/[0.02] text-slate-300 hover:border-[var(--line-strong)]'
                }`}
              >
                Nearest {m}s
              </button>
            ))}
          </div>

          <button
            onClick={trigger}
            disabled={!valid}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-400 text-[#06281b] font-semibold hover:bg-emerald-300 transition shadow-lg shadow-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Zap className="w-4 h-4" />
            Trigger Snapping
          </button>

          <div className="space-y-2 pt-2">
            <div className="text-xs uppercase tracking-wider text-slate-400">Step-by-Step Solver</div>
            <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
              <span className="text-slate-500">1.</span> input = {valid ? num : '—'}
            </div>
            <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
              <span className="text-slate-500">2.</span> divide by {mode} → {valid ? (num / mode).toFixed(4) : '—'}
            </div>
            <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
              <span className="text-slate-500">3.</span> round half-up → {valid ? Math.round(num / mode) : '—'}
            </div>
            <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/[0.06] px-3.5 py-2.5 font-mono-math text-sm text-emerald-200">
              <span className="text-emerald-400/80">=</span> multiply by {mode} → {valid ? rounded : '—'}
            </div>
            <div className="mt-2 flex flex-wrap items-start gap-2">
              <CopyButton markdown={md} />
              <EmailSolutionButton title="Rounding Numbers Calculator" markdown={md} />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--line)] bg-[#0a0e1a] p-4 blueprint-grid">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            <Target className="w-3 h-3" /> Snapping Number Line
          </div>
          <svg viewBox={`0 0 ${vw} ${vh}`} className="w-full h-auto">
            {/* Slope curve */}
            <path d={`M ${padX - 10} 50 Q ${vw / 2} 30, ${vw - padX + 10} 50 L ${vw - padX + 10} ${baseY} L ${padX - 10} ${baseY} Z`} fill="rgba(110,231,255,0.05)" stroke="#1e293b" strokeWidth={1} />
            <path d={`M ${padX - 10} 50 Q ${vw / 2} 30, ${vw - padX + 10} 50`} fill="none" stroke="#6ee7ff" strokeWidth={1.5} strokeDasharray="3 3" opacity={0.5} />

            {/* Number line */}
            <line x1={padX} y1={baseY} x2={vw - padX} y2={baseY} stroke="#38e1a4" strokeWidth={2} />

            {/* Anchors */}
            {anchors.map((a) => {
              const x = xFor(a)
              const isTarget = a === rounded && valid
              return (
                <g key={a}>
                  <line x1={x} y1={baseY - 8} x2={x} y2={baseY + 8} stroke={isTarget ? '#fbbf24' : '#475569'} strokeWidth={isTarget ? 2 : 1} />
                  <circle cx={x} cy={baseY} r={isTarget ? 5 : 3} fill={isTarget ? '#fbbf24' : '#1e293b'} stroke={isTarget ? '#fbbf24' : '#475569'} strokeWidth={1} />
                  <text x={x} y={baseY + 22} textAnchor="middle" fontSize="10" fontFamily="JetBrains Mono" fill={isTarget ? '#fbbf24' : '#64748b'}>{a}</text>
                </g>
              )
            })}

            {/* Source marker */}
            {valid && (
              <g>
                <line x1={ballStartX} y1={baseY - 50} x2={ballStartX} y2={baseY - 12} stroke="#fb7185" strokeWidth={1} strokeDasharray="2 3" />
                <text x={ballStartX} y={baseY - 56} textAnchor="middle" fontSize="10" fontFamily="JetBrains Mono" fill="#fb7185">{num}</text>
              </g>
            )}

            {/* Ball */}
            {valid && (
              <g key={snapKey}>
                <circle
                  cx={snapped ? ballEndX : ballStartX}
                  cy={snapped ? baseY - 6 : 56}
                  r={9}
                  fill="#38e1a4"
                  stroke="#fff"
                  strokeWidth={2}
                  style={{
                    transition: snapped ? 'cx 0.9s cubic-bezier(0.6,0.05,0.3,1), cy 0.9s cubic-bezier(0.6,0.05,0.3,1)' : 'none',
                    filter: 'drop-shadow(0 0 6px rgba(56,225,164,0.6))',
                  }}
                />
              </g>
            )}
          </svg>
          <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded-md bg-white/[0.02] border border-[var(--line)] py-2"><div className="text-slate-500">Input</div><div className="font-mono-math text-rose-300">{valid ? num : '—'}</div></div>
            <div className="rounded-md bg-white/[0.02] border border-[var(--line)] py-2"><div className="text-slate-500">Mode</div><div className="font-mono-math text-cyan-300">×{mode}</div></div>
            <div className="rounded-md bg-white/[0.02] border border-[var(--line)] py-2"><div className="text-slate-500">Snap</div><div className="font-mono-math text-emerald-300">{valid ? rounded : '—'}</div></div>
          </div>
        </div>
      </div>
    </SolverCard>
  )
})

RoundingCalculator.displayName = 'RoundingCalculator'
export default RoundingCalculator
